import { UNGROUPED_RESOURCES_GROUP_ID } from '@/console/features/groups/components/group-list/group-list.constants';

import { api } from '../../_generated/api/assets-api.generated';

export const projectsApi = api.enhanceEndpoints({
  addTagTypes: ['Project', 'ProjectVersions', 'Groups', 'Device'],
  endpoints: {
    ReadAllProjects: {
      providesTags: (_, __, args) => {
        const tags = [{ type: 'Project' as const, id: 'LIST' }];
        const filteredGroup = args?.filter?.groupId;
        const groupId = filteredGroup?.contains ?? UNGROUPED_RESOURCES_GROUP_ID;
        if (filteredGroup) {
          return [...tags, { type: 'Groups' as const, id: groupId }];
        }
        return tags;
      },
    },
    ReadProjectsForSelect: {
      providesTags: (_, __, args) => {
        const tags = [{ type: 'Project' as const, id: 'LIST' }];
        const filteredGroup = args?.filter?.groupId;
        const groupId = filteredGroup?.contains ?? UNGROUPED_RESOURCES_GROUP_ID;
        if (filteredGroup) {
          return [...tags, { type: 'Groups' as const, id: groupId }];
        }
        return tags;
      },
    },
    ReadProjectsForList: {
      providesTags: (_, __, args) => {
        const tags = [{ type: 'Project' as const, id: 'LIST' }];
        const filteredGroup = args?.filter?.groupId;
        const groupId = filteredGroup?.contains ?? UNGROUPED_RESOURCES_GROUP_ID;
        if (filteredGroup) {
          return [...tags, { type: 'Groups' as const, id: groupId }];
        }
        return tags;
      },
    },
    // CompleteMultipartUpload is untagged on purpose: it fires before the S3
    // listener writes the version row, so the asset event invalidates instead.
    ReadProject: {
      providesTags: (_, __, arg) => {
        return [{ type: 'Project' as const, id: arg.projectId }];
      },
    },
    ReadVersionsByIds: {
      // The generated arg type widens a list variable to `string | string[]`.
      providesTags: (_, __, arg) =>
        [arg.projectIds].flat().map((projectId) => ({
          type: 'ProjectVersions' as const,
          id: projectId,
        })),
    },
    ReadVersionsForProject: {
      providesTags: (_, __, arg) => {
        return [
          { type: 'Project' as const, id: arg.projectId },
          {
            type: 'ProjectVersions' as const,
            id: arg.projectId,
          },
        ];
      },
    },
    ReadLatestActiveVersion: {
      providesTags: (_, __, arg) => {
        return [
          { type: 'Project' as const, id: arg.projectId },
          {
            type: 'ProjectVersions' as const,
            id: arg.projectId,
          },
        ];
      },
    },
    ReadLatestVersion: {
      providesTags: (_, __, arg) => {
        return [
          { type: 'Project' as const, id: arg.projectId },
          {
            type: 'ProjectVersions' as const,
            id: arg.projectId,
          },
        ];
      },
    },
    CreateProject: {
      invalidatesTags: (result, _, __) => {
        return [
          { type: 'Project' as const, id: 'LIST' },
          { type: 'Groups' as const, id: result?.createProject.groupId },
        ];
      },
    },
    DeleteProject: {
      invalidatesTags: [
        { type: 'Project' as const, id: 'LIST' },
        'Groups' as const,
      ],
    },
    DeleteProjectVersion: {
      invalidatesTags: (_, __, arg) => [
        { type: 'Project' as const, id: arg.projectId },
        { type: 'ProjectVersions' as const, id: arg.projectId },
        'Groups' as const,
      ],
    },
    UpdateProject: {
      invalidatesTags: (_, __, arg) => [
        { type: 'Project' as const, id: arg.projectId },
        { type: 'Project' as const, id: 'LIST' },
        'Groups' as const,
      ],
    },
    UpdateProjectVersion: {
      invalidatesTags: (_, __, arg) => [
        { type: 'ProjectVersions' as const, id: arg.projectId },
        { type: 'Project' as const, id: arg.projectId },
      ],
    },
    ReadLinkedLibrariesForProject: {
      providesTags: (_, __, arg) => {
        return [{ type: 'Project' as const, id: arg.projectId }];
      },
    },
    ReadLinkedDevicesForProject: {
      providesTags: (result, __, arg) => {
        const devices = result?.readLinkedDevicesForProject?.items || [];
        const deviceTags = devices.map((device) => ({
          type: 'Device' as const,
          id: device.deviceId,
        }));

        return [
          { type: 'Project' as const, id: arg.projectId },
          {
            type: 'ProjectVersions' as const,
            id: arg.projectId,
          },
          ...deviceTags,
        ];
      },
    },
  },
});

export const {
  useCreateProjectMutation,
  useExportProjectsCsvMutation,
  useReadAllProjectsQuery,
  useReadProjectsForListQuery,
  useReadProjectsForSelectQuery,
  useDeleteProjectMutation,
  useCompleteProjectMultipartUploadMutation,
  useLazyDownloadProjectVersionQuery,
  useReadVersionsForProjectQuery,
  useReadVersionsByIdsQuery,
  useUpdateProjectMutation,
  useDeleteProjectVersionMutation,
  useCreateProjectVersionMutation,
  useReadProjectQuery,
  useLazyReadProjectQuery,
  useReadLatestActiveVersionQuery,
  useReadLatestVersionQuery,
  useUpdateProjectVersionMutation,
  useReadLinkedLibrariesForProjectQuery,
  useReadLinkedDevicesForProjectQuery,
  useReadProjectNamesForRoleRowQuery,
  useReadProjectsByIdsQuery,
} = projectsApi;
